import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import {
  CURRENCIES, joinCurrencies, joinThreshold, parseCurrencies, parseThreshold, procurementAlertDays, settingsProblem,
} from "../lib/procurement";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";

// Paramètres › Achats et actifs (Ordre 20-4): the currencies a request may use, the amount under which the buyers may decide without an
// approver (D-31), and when the assets' deadlines (warranty, renewal, loan return) are announced. settings:view to read, settings:manage to change.
export default function SettingsProcurement() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [selected, setSelected] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [days, setDays] = useState("");
  const [holder, setHolder] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => {
    setSaved(settings);
    setSelected(parseCurrencies(settings.currencies));
    setThresholds(parseThreshold(settings.approval_threshold));
    setDays(settings.alert_days);
    setHolder(!!settings.alert_holder);
  };
  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/procurement");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const next = saved == null ? null : {
    currencies: joinCurrencies(selected), approval_threshold: joinThreshold(thresholds, selected), alert_days: procurementAlertDays(days), alert_holder: holder,
  };
  const problem = settingsProblem(selected, thresholds, days);
  const dirty = next != null && Object.keys(next).some((key) => next[key] !== saved[key]);
  const toggle = (code) => setSelected((list) => (list.includes(code) ? list.filter((c) => c !== code) : [...list, code]));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/procurement", next);
      apply(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-procurement">
      <SectionHeader title="Achats et actifs" description="Les devises des demandes d'achat, le seuil d'approbation et les alertes sur les échéances du matériel." />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Devises acceptées</h3>
            <p className="text-xs text-slate-500">Celles que l'on peut choisir sur une demande d'achat. Une demande déjà faite garde sa devise.</p>
          </div>
          <div className="grid grid-cols-3 gap-2" role="group" aria-label="Devises acceptées">
            {CURRENCIES.map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm border border-slate-200 rounded-md px-3 py-2">
                <Checkbox checked={selected.includes(code)} onCheckedChange={() => toggle(code)} disabled={!canManage} data-testid={`procurement-currency-${code}`} />
                {code}
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Seuil d'approbation</h3>
            <p className="text-xs text-slate-500">
              Sous ce montant (celui du devis retenu), la gestion des achats peut décider seule. Laissez vide pour qu'un approbateur décide toujours.
              Le demandeur et la personne qui a saisi le devis retenu ne décident jamais, quel que soit le montant.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {selected.length === 0 && <p className="text-xs text-slate-500">Choisissez d'abord une devise.</p>}
            {CURRENCIES.filter((c) => selected.includes(c)).map((code) => (
              <div key={code} className="space-y-1.5">
                <label htmlFor={`procurement-threshold-${code}`} className="text-sm font-medium text-slate-900">Seuil en {code}</label>
                <Input id={`procurement-threshold-${code}`} inputMode="numeric" value={thresholds[code] ?? ""} disabled={!canManage} placeholder="Aucun seuil"
                  onChange={(e) => setThresholds((t) => ({ ...t, [code]: e.target.value }))} data-testid={`procurement-threshold-${code}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="procurement-alert-days" className="text-sm font-medium text-slate-900">Prévenir avant l'échéance</label>
          <div className="flex flex-wrap items-center gap-2">
            <Input id="procurement-alert-days" value={days} disabled={!canManage} className="w-40" onChange={(e) => setDays(e.target.value)}
              aria-invalid={!!problem && problem.includes("jours")} data-testid="procurement-alert-days" />
            <span className="text-sm text-slate-500">jours avant, séparés par des virgules (le jour même s'y ajoute toujours)</span>
          </div>
        </div>
        <label className="flex items-start gap-3 border-t border-slate-100 pt-4">
          <Switch checked={holder} onCheckedChange={setHolder} disabled={!canManage} data-testid="procurement-alert-holder" />
          <span className="text-sm">
            <span className="font-medium text-slate-900">Prévenir aussi la personne qui a l'actif en main</span>
            <span className="block text-xs text-slate-500">Avant le retour d'un prêt seulement. Les gestionnaires du matériel sont toujours prévenus.</span>
          </span>
        </label>
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4 border-t border-slate-100 pt-3">
          <li>Trois échéances sont suivies : fin de garantie, renouvellement (licence, abonnement) et retour d'un prêt.</li>
          <li>Le message donne l'actif, la nature et la date, jamais son coût ni son numéro de série. Chaque alerte ne part qu'une fois ; une nouvelle date en déclenche de nouvelles.</li>
          <li>Le canal WhatsApp et l'événement « Échéance d'un actif » se règlent dans Notifications.</li>
        </ul>
      </div>
      {problem && dirty && <p className="text-sm text-red-600" role="alert" data-testid="procurement-settings-problem">{problem}</p>}
      <SaveBar dirty={dirty} saveDisabled={!!problem} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
